import React from 'react'
import { Line } from 'react-chartjs-2'
import moment from 'moment'

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Ags', 'Sep', 'Okt', 'Nov', 'Des']

const YEARS_BACK = 5

const formatIdr = (value) => {
  if (value >= 1e12) return `${(value / 1e12).toFixed(1)} T`
  if (value >= 1e9) return `${(value / 1e9).toFixed(1)} M`
  if (value >= 1e6) return `${(value / 1e6).toFixed(0)} jt`
  return value.toLocaleString('id-ID')
}

/**
 * US-DB-3 — dual-line monthly chart: order count (left axis) and order value
 * in IDR (right axis), with its own year dropdown (AC-5/AC-6). Hover shows
 * exact values (AC-7). Current year shows Jan..current month.
 */
export default function OrderTrendChart ({ data, loading, year, onYearChange }) {
  const currentYear = moment().year()
  const years = []
  for (let y = currentYear; y > currentYear - YEARS_BACK; y--) {
    years.push(y)
  }

  let body = null
  if (loading && !data) {
    body = <div className='dbv2-loading'>Memuat…</div>
  } else if (data) {
    let months = data.months || []
    if (data.year === currentYear) {
      months = months.slice(0, moment().month() + 1)
    }
    const hasData = months.some((m) => m.count > 0 || m.value > 0)
    if (!hasData) {
      body = <div className='dbv2-empty'>Tidak ada data untuk periode yang dipilih.</div>
    } else {
      const chartData = {
        labels: months.map((m) => MONTH_LABELS[m.month - 1]),
        datasets: [
          {
            label: 'Jumlah Order',
            yAxisID: 'count',
            data: months.map((m) => m.count),
            fill: false,
            borderColor: '#2F6FED',
            backgroundColor: '#2F6FED',
            borderWidth: 4,
            pointRadius: 3.5,
            lineTension: 0.25
          },
          {
            label: 'Nilai (IDR)',
            yAxisID: 'value',
            data: months.map((m) => m.value),
            fill: false,
            borderColor: '#F79009',
            backgroundColor: '#F79009',
            borderWidth: 4,
            borderDash: [6, 3],
            pointRadius: 3,
            lineTension: 0.25
          }
        ]
      }
      const options = {
        maintainAspectRatio: false,
        // Canvas legend symbols are too small to show the dash pattern, so the
        // legend is rendered as HTML below the chart instead.
        legend: { display: false },
        tooltips: {
          callbacks: {
            label: (item, chart) => {
              const label = chart.datasets[item.datasetIndex].label
              return item.datasetIndex === 1
                ? `${label}: Rp ${Number(item.yLabel).toLocaleString('id-ID')}`
                : `${label}: ${item.yLabel}`
            }
          }
        },
        scales: {
          yAxes: [
            {
              id: 'count',
              position: 'left',
              ticks: { beginAtZero: true, fontColor: '#2F6FED', fontSize: 10, precision: 0 },
              gridLines: { color: '#F2F4F7' }
            },
            {
              id: 'value',
              position: 'right',
              ticks: {
                beginAtZero: true,
                fontColor: '#F79009',
                fontSize: 10,
                callback: (value) => formatIdr(value)
              },
              gridLines: { display: false }
            }
          ],
          xAxes: [{ gridLines: { display: false }, ticks: { fontSize: 10 } }]
        }
      }
      body = (
        <div>
          <div style={{ height: 240 }}>
            <Line data={chartData} options={options} />
          </div>
          <div className='dbv2-trend-legend'>
            <span className='dbv2-trend-legend-item'>
              <span className='dbv2-trend-legend-line' style={{ borderTop: '4px solid #2F6FED' }} />
              Jumlah Order
            </span>
            <span className='dbv2-trend-legend-item'>
              <span className='dbv2-trend-legend-line' style={{ borderTop: '4px dashed #F79009' }} />
              Nilai (IDR)
            </span>
          </div>
        </div>
      )
    }
  }

  return (
    <div className='dbv2-card'>
      <div className='dbv2-chart-head'>
        <div>
          <p className='dbv2-card-title' style={{ marginBottom: 2 }}>Tren Order</p>
          <p className='dbv2-card-subtitle' style={{ marginBottom: 0 }}>Jumlah order & nilai (IDR) per bulan</p>
        </div>
        <div className='dbv2-chart-filter'>
          <label>Tahun:</label>
          <select value={year} onChange={(e) => onYearChange(Number(e.target.value))}>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>
      {body}
    </div>
  )
}
